import { useEffect, useMemo, useState } from "react";
import { YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import {
  SmsInviteOpenParams,
  buildSmsInviteLiffUrl,
  buildSmsInviteLineSchemeUrl,
  buildSmsInviteBrowserPath,
  shouldAutoOpenLiff,
  markSmsInviteAutoOpened,
} from "@utils/smsInviteOpen";
import { trackGa } from "@lib/tracker";

/**
 * 簡訊邀請中繼畫面（/sms-invite/open 與短連結 /s/{token} 共用）。
 *
 * 拿到一組有效的邀請參數之後要做的事都在這裡：
 *   - 沒有瓦斯行 LIFF ID → 直接導回本站 /sms-invite（退回預設 LIFF）
 *   - 有 → 自動嘗試開 LINE 一次，同時留一顆「在 LINE 中開啟」按鈕與「在瀏覽器繼續」
 *
 * 自動嘗試用 `line://app/…` 自訂 scheme，不用 https 的 liff.line.me：iOS 對 Universal Link
 * 只在使用者親手點時才喚起 App，程式改網址會被當成一般網頁在 Safari 載入，接著 LIFF
 * 在外部瀏覽器走 LINE Login，客戶就卡在 access.line.me 登入頁（2026-09-24 實測）。
 * 自訂 scheme 導向失敗（沒裝 LINE）時頁面留在原地，所以按鈕與瀏覽器退路仍看得到；
 * 按鈕本身維持 https 版，親手點 Universal Link 是最穩的路。
 */
const SmsInviteRelayView = ({ params }: { params: SmsInviteOpenParams }) => {
  const liffUrl = useMemo(() => buildSmsInviteLiffUrl(params), [params]);
  const lineSchemeUrl = useMemo(() => buildSmsInviteLineSchemeUrl(params), [params]);
  const browserPath = useMemo(() => buildSmsInviteBrowserPath(params), [params]);
  const [autoTried, setAutoTried] = useState(false);

  useEffect(() => {
    if (!liffUrl) {
      trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "no_liff_id" });
      window.location.replace(browserPath);
      return;
    }
    trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "shown" });
    if (lineSchemeUrl && shouldAutoOpenLiff()) {
      markSmsInviteAutoOpened(params.invite);
      setAutoTried(true);
      window.location.href = lineSchemeUrl;
    }
  }, [params, liffUrl, lineSchemeUrl, browserPath]);

  if (!liffUrl) return null;

  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} px="$6" gap="$5">
      <YStack ai="center" gap="$2">
        <Text fontSize={20} fontWeight="700" color={colors.grayDark} textAlign="center">
          在 LINE 中完成註冊
        </Text>
        <Text fontSize={14} color={colors.gray} textAlign="center" lineHeight={22}>
          {autoTried ? "如果 LINE 沒有自動打開，請點下方按鈕。" : "請點下方按鈕，用 LINE 開啟邀請。"}
        </Text>
      </YStack>

      <a
        href={liffUrl}
        onClick={() => trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "tap_open_line" })}
        style={{ textDecoration: "none", width: "100%", maxWidth: 360 }}
      >
        <XStack
          ai="center"
          jc="center"
          bg="#06C755"
          br={12}
          py="$4"
          px="$5"
          gap="$2"
          pressStyle={{ opacity: 0.85 }}
        >
          <Text fontSize={17} fontWeight="700" color={colors.light}>
            在 LINE 中開啟
          </Text>
        </XStack>
      </a>

      <a
        href={browserPath}
        onClick={() => trackGa("funnel_step", { funnel: "sms_invite", step: "open_relay", result: "tap_browser" })}
        style={{ textDecoration: "none" }}
      >
        <Text fontSize={13} color={colors.primary} textAlign="center">
          沒有安裝 LINE？在瀏覽器繼續
        </Text>
      </a>
    </YStack>
  );
};

export default SmsInviteRelayView;
